import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { RobotIcon as Robot, ArrowsClockwiseIcon as ArrowsClockwise, EnvelopeIcon as Envelope, ClockIcon as Clock, StorefrontIcon as Storefront, VideoCameraIcon as VideoCamera, CoinsIcon as Coins } from "@phosphor-icons/react";
import { Icon, GoldDivider } from "@/components/luxury";

/**
 * Admin → Automations console.
 *
 * Single page that exposes the three operator-experience automations
 * (reference-followup, ops-digest, stale-listings) for manual on-demand
 * runs. Each card shows a one-line "what it does" and a "Run now"
 * button; the response payload renders inline so admins can confirm
 * what fired without opening logs.
 *
 * The same automations also run on their own background loops; this
 * page is for triage / debugging / first-time-seeing-it-work moments.
 */
const CARDS = [
  {
    name: "reference-followup",
    title: "Reference-request follow-ups",
    icon: Envelope,
    blurb:
      "Walks every kennel verification and (a) nudges buyers who haven't responded after 5 days, (b) marks references as 'non-responsive' after 14 days. Pings ops on Discord on time-out.",
    cadence: "Runs once per day automatically.",
  },
  {
    name: "ops-digest",
    title: "Daily ops digest",
    icon: Clock,
    blurb:
      "Composes and dispatches the daily 08:00 UTC summary: new sign-ups, new dogs, new listings, escrow movement, in-flight verifications, unacked anomalies. Posts to Discord + emails admins who opted in.",
    cadence: "Runs once per day at 08:00 UTC.",
  },
  {
    name: "stale-listings",
    title: "Stale-listing flagger",
    icon: Storefront,
    blurb:
      "Marketplace listings untouched for 60 days get a 'still available?' email. After 90 days, they're auto-paused (one-click reactivate from the email).",
    cadence: "Runs once per day automatically.",
  },
  {
    name: "release-tour-payouts",
    title: "Release pending video-tour payouts",
    icon: VideoCamera,
    blurb:
      "Walks every paid video tour whose 90% seller share is still accruing (because the seller hadn't finished Stripe Connect when the tour ran), retries the Connect transfer, and settles whatever is now possible.",
    cadence: "Runs once per day automatically.",
  },
  {
    name: "reverify-expiring",
    title: "Re-verify expiring kennels",
    icon: Robot,
    blurb:
      "30 days before a kennel's verified-until date → emails the owner one renewal warning. On expiry → revokes the seal, resets the verification doc to in-review, and pings ops on Discord.",
    cadence: "Runs once per day automatically.",
  },
  {
    name: "referral-payouts",
    title: "Buyer Pass referral payouts",
    icon: Coins,
    blurb:
      "Walks every Buyer Pass subscription with a referrer kennel attached and transfers 20% of each in-window invoice (12 months from original subscribe) to the kennel's Stripe Connect account. See the full leaderboard at /admin/referrals.",
    cadence: "Runs once per day automatically.",
  },
];

export default function AdminAutomations() {
  const [busy, setBusy] = useState({});
  const [last, setLast] = useState({});
  const [list, setList] = useState([]);

  useEffect(() => {
    api.get("/admin/automations/list")
      .then((r) => setList(r.data?.automations || []))
      .catch(() => {});
  }, []);

  const runNow = async (name) => {
    setBusy((s) => ({ ...s, [name]: true }));
    try {
      const r = await api.post(`/admin/automations/${name}/run-now`);
      setLast((s) => ({ ...s, [name]: r.data }));
      toast.success(`${name} ran successfully`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy((s) => ({ ...s, [name]: false }));
    }
  };

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3">Admin / Automations</p>
        <h1 className="font-spectral font-light text-4xl sm:text-5xl tracking-tight leading-[1.05]">
          Operator automations
        </h1>
        <p className="text-sm text-[#6B6B6B] mt-3 max-w-2xl leading-relaxed">
          Three background crons that take repetitive admin work off your plate.
          You can trigger any of them on demand below — useful for debugging or
          when you just want to see what's currently in flight.
        </p>
        <GoldDivider className="mt-8 mb-10 max-w-md" />

        <div className="space-y-5">
          {CARDS.map((c) => {
            const seen = list.find((x) => x.name === c.name);
            return (
              <div
                key={c.name}
                className="bg-white border border-[#EAEAEA] rounded-xl p-6 shadow-luxury-light"
                data-testid={`automation-card-${c.name}`}
              >
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-full bg-cream ring-1 ring-[#E2DAC2] flex items-center justify-center flex-shrink-0">
                    <Icon as={c.icon} tone="gold" size="md" weight="duotone" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <h3 className="font-spectral text-xl tracking-tight">{c.title}</h3>
                        <p className="text-[11px] uppercase tracking-[0.18em] text-[#6B6B6B] mt-1">{c.cadence}</p>
                      </div>
                      <Button
                        size="sm"
                        className="rounded-full"
                        onClick={() => runNow(c.name)}
                        disabled={!!busy[c.name]}
                        data-testid={`automation-run-${c.name}`}
                      >
                        <ArrowsClockwise size={14} className="mr-1.5" />
                        {busy[c.name] ? "Running…" : "Run now"}
                      </Button>
                    </div>
                    <div className="gold-hairline my-4" />
                    <p className="text-sm text-[#3a3a3a] leading-relaxed">{c.blurb}</p>
                    {(last[c.name] || seen?.last_run) && (
                      <pre
                        className="mt-4 text-[11px] bg-[#FAFAFA] border border-[#EAEAEA] rounded p-3 font-mono text-[#3a3a3a] overflow-x-auto"
                        data-testid={`automation-last-${c.name}`}
                      >
                        {JSON.stringify(last[c.name] || seen?.last_run, null, 2)}
                      </pre>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-[11px] text-[#6B6B6B] italic text-center mt-10 flex items-center justify-center gap-2">
          <Robot size={14} weight="duotone" /> Cron loops run automatically — these manual triggers are for debugging or one-off pulls.
        </p>
      </div>
    </AppShell>
  );
}
